import { cleanFullAddress } from '@urbanx/agx-ui-components';
import { useEffect, useId, useState } from 'react';
import { useFeatureToggles, useSelectedCampaign, useSelectedContract } from 'hooks';
import { MenuTitles, Pages } from 'constants/menu';
import { ContractSubPage } from '../ContractSubPage';
import TerminateContractAction from '../TerminateContractAction';
import { getBuyerDetailsSubPages } from './BuyerDetailsSubPages';
import {
  BuyerModel,
  CampaignDetailModelState,
  EditScope,
  StakeholderType,
  useInvalidateUseStaffGetBuyers,
  useStaffGetBuyers,
  useStaffUpdateBuyers,
  VendorBuyerTypes,
} from '@urbanx/agx.campaigns.client';
import { ErrorModal } from 'helpers/ErrorModal';
import { isDirtyEqual } from 'utils/compares';
import {
  getBuyerNames,
  normalizeBuyers,
  sortBuyers,
} from 'pages/contracts/components/buyers-details/BuyersHelper';
import { useGetListingByCampaignId } from '@urbanx/agx.rex.client';
import {
  getVendorBuyerAddress,
  RexDisconnectedModalFields,
  vendorBuyerAddressesChanged,
} from 'pages/campaigns/components/manageCampaign/RexDataUpdateHelper';
import { isEqual } from 'lodash-es';
import useRexDisconnectedModal from 'pages/campaigns/components/manageCampaign/useRexDisconnectedModal';
import { AgxPanel, AgxVendorBuyers, type VendorBuyerValue } from '@urbanx/agx.ui-components2';

const ignoredBuyerPaths: string[] = [
  'type',
  'stakeholderType',
  'individual.additionalCcEmails',
  'individual.legalName',
  'individual.selectedLegalNames',
  'individual.address.sameAddress',
  'individual.address.address.formattedAddress',
];

const BuyersInformation = () => {
  const buyerInformationId = useId();
  const { campaign } = useSelectedCampaign();
  const { contract } = useSelectedContract();
  const [isLoaded, setIsLoaded] = useState(false);
  const [validate, setValidate] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [submitFormErrors, setSubmitFormErrors] = useState<string[]>([]);
  const invalidateUseStaffGetBuyers = useInvalidateUseStaffGetBuyers();
  const { featureToggles } = useFeatureToggles();
  const hideIdentityVerification = featureToggles?.identityVerification !== true;

  const {
    data: buyersResponse,
    isPending,
    isError,
  } = useStaffGetBuyers(
    contract?.contractId || '',
    !!contract?.contractId && !!campaign?.campaignId
  );
  const buyers = buyersResponse?.buyers;

  const [value, setValue] = useState<VendorBuyerValue[]>(
    normalizeBuyers(buyers) as VendorBuyerValue[]
  );
  const { modal: rexDisconnectedModal, openWithChanges } = useRexDisconnectedModal();

  useEffect(() => {
    setValue(normalizeBuyers(buyers) as VendorBuyerValue[]);
    setIsLoaded(!isPending);
  }, [buyers, isPending]);

  useEffect(() => {
    const formBuyers = sortBuyers(normalizeBuyers(value));
    const apiBuyers = sortBuyers(normalizeBuyers(buyers));

    setValidate(!isDirtyEqual(formBuyers, apiBuyers, ignoredBuyerPaths));
  }, [value, buyers]);

  const { staffUpdateBuyers, staffUpdateBuyersPending } = useStaffUpdateBuyers({
    onSuccess: () => {},
    onError: () => {},
  });

  const { data: rexListing, isPending: isPendingRexListing } = useGetListingByCampaignId(
    campaign?.campaignId || '',
    campaign?.campaignId !== undefined
  );
  const exportedIdentityVerificationSourceIds =
    buyersResponse?.exportedIdentityVerificationSourceIds ?? [];

  const isFormDirty = !isDirtyEqual(
    sortBuyers(normalizeBuyers(value)),
    sortBuyers(normalizeBuyers(buyers)),
    ignoredBuyerPaths
  );

  const onSave = async (success: () => void) => {
    if (!value || !contract?.contractId) return;

    if (!rexListing && isPendingRexListing === false) {
      const prevBuyers = normalizeBuyers(buyers);
      const newBuyers = normalizeBuyers(value);

      const prevBuyersNames = getBuyerNames(prevBuyers);
      const newBuyersNames = getBuyerNames(newBuyers);

      openWithChanges([
        ...(!isEqual(prevBuyersNames, newBuyersNames)
          ? [
              {
                field: RexDisconnectedModalFields.BuyersLegalNames,
                value: newBuyersNames,
              },
            ]
          : []),
        ...(vendorBuyerAddressesChanged(prevBuyers, newBuyers)
          ? [
              {
                field: RexDisconnectedModalFields.BuyersAddress,
                value: cleanFullAddress(
                  getVendorBuyerAddress(newBuyers[0])?.address ??
                    getVendorBuyerAddress(newBuyers[0])?.alternate
                ),
              },
            ]
          : []),
      ]);
    }

    await staffUpdateBuyers(
      {
        body: value as BuyerModel[],
        contractId: contract.contractId,
      },
      {
        onSuccess: async (data) => {
          if (data?.success) {
            success();
            await invalidateUseStaffGetBuyers(contract.contractId);
          } else {
            setSubmitFormErrors(data?.errors ?? []);
            setOpenErrorModal(true);
          }
        },
      }
    );
  };

  if (!isLoaded) return null;

  return (
    <ContractSubPage
      key={value ? buyerInformationId : undefined}
      pageTitle="Buyer Details"
      secondaryActiveMenu={MenuTitles.BUYERS_DETAILS}
      currentPage={Pages.BUYERS_INFORMATION}
      subPages={getBuyerDetailsSubPages(campaign?.state)}
      onSaveChanges={onSave}
      isFormDirty={isFormDirty}
      isSaving={staffUpdateBuyersPending}
      isError={isError}
      secondaryActions={<TerminateContractAction />}
    >
      {!isPending && value && (
        <AgxPanel id="buyer-information-panel">
          <AgxVendorBuyers
            id="BuyerInformation"
            onChange={({ value: nextValue }) => setValue(nextValue)}
            propertyAddress={campaign?.address}
            defaultValue={value ?? []}
            editScope={EditScope.FullEdit}
            stakeholderType={StakeholderType.Buyer}
            vendorBuyerTypes={[
              VendorBuyerTypes.Individual,
              VendorBuyerTypes.PowerOfAttorney,
              VendorBuyerTypes.Company,
            ]}
            companyVendorBuyerTypes={[
              VendorBuyerTypes.Individual,
              VendorBuyerTypes.PowerOfAttorney,
              VendorBuyerTypes.Company,
            ]}
            vendorBuyerConfig={{
              preferredNameRequired: true,
              addressRequired: campaign?.state === CampaignDetailModelState.NSW,
              emailRequired: campaign?.state === CampaignDetailModelState.NSW,
              phoneNumberRequired: campaign?.state === CampaignDetailModelState.NSW,
              excludeMiddleName: false,
              displayAddCc: false,
              hideIdentityVerification,
              hideCompletedIdentityVerification: true,
            }}
            powerOfAttorneyConfig={{
              hideDocumentationUpload: true,
            }}
            companyConfig={{
              hideAbn: false,
              hideAcn: false,
              hideRegisteredForGst: true,
              requireAcn: true,
              requireAbn: false,
            }}
            required
            campaignId={campaign?.campaignId || ''}
            displayLegalName={false}
            agents={[]}
            leadAgentId={''}
            validate={validate}
            exportedIdentityVerificationSourceIds={exportedIdentityVerificationSourceIds}
          />
        </AgxPanel>
      )}
      {openErrorModal && (
        <ErrorModal errorList={submitFormErrors} setShowModal={setOpenErrorModal} />
      )}
      {rexDisconnectedModal}
    </ContractSubPage>
  );
};

export default BuyersInformation;
